export const getLabelQuantity = (num) =>
  num / 1000 > 1 ? `${Math.floor(num / 1000)}K` : num;

const getCurrencySymbol = (currency = 'USD') => {
  try {
    return (
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol'
      })
        .formatToParts(1)
        .find((part) => part.type === 'currency')?.value || '$'
    );
  } catch {
    return '$';
  }
};

export const formatCurrency = (num, currency = 'USD') => {
  const symbol = getCurrencySymbol(currency);
  if (num === null || num === undefined) return 'Infinite';
  if (num === 0) return `${symbol}0`;
  if (num / 1000 >= 1) {
    const k = Math.floor(num / 1000);
    return `${symbol}${k}k`;
  }
  return `${symbol}${num}`;
};

export const formatCompactCurrency = (num, currency = 'USD') => {
  if (num === null || num === undefined) return 'Infinite';
  if (num === 0) return `${currency} 0`;
  if (num >= 1000000) {
    return `${currency} ${(num / 1000000).toFixed(1)}m`;
  }
  if (num >= 1000) {
    return `${currency} ${(num / 1000).toFixed(1)}k`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

export const formatCurrencyFull = (num, currency = 'USD') => {
  if (num === null || num === undefined) return 'Infinite';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

export const getLabelTransform = (index, total) => {
  if (index === 0) return 'translateX(0%)';
  if (index === total - 1) return 'translateX(-100%)';
  return 'translateX(-50%)';
};

export const getLowerRange = (prevUpper) => {
  if (prevUpper === null || prevUpper === undefined) return null;
  return prevUpper + 1;
};

export const getTiersChart = (list) => {
  const totalStores =
    list?.reduce((sum, item) => sum + (item?.storesAssociated || 0), 0) ?? 0;

  return {
    totalStores: getLabelQuantity(totalStores),
    list: list.map((item) => ({
      ...item,
      size: totalStores ? (item.storesAssociated / totalStores) * 100 : 0,
      quantityLabel: getLabelQuantity(item?.storesAssociated || 0)
    }))
  };
};

export const mapTiersToLegendItems = (tiers) => {
  return tiers.map((tier) => ({
    color: `#${tier.color}`,
    label: tier.name,
    quantity: tier.storesAssociated
  }));
};

export const getCurrencyTiersData = (data, currency = 'USD') => {
  if (!data || data.length === 0) return {list: [], boundaryLabels: []};

  const withRanges = data.map((item, index) => {
    let lowerRange;
    if (item.lowerRange !== undefined) {
      lowerRange = item.lowerRange;
    } else if (index === 0) {
      lowerRange = 0;
    } else {
      lowerRange = getLowerRange(data[index - 1].upperRange);
    }
    return {
      ...item,
      lowerRange,
      isInfinite: item.upperRange === null || item.upperRange === undefined
    };
  });

  const maxFiniteRange = Math.max(
    ...withRanges
      .filter((t) => !t.isInfinite)
      .map((t) => t.upperRange - t.lowerRange)
  );

  const list = withRanges.map((item) => {
    const range = item.isInfinite
      ? maxFiniteRange
      : item.upperRange - item.lowerRange;
    return {
      ...item,
      size: range,
      quantityLabel: item.name
    };
  });

  const totalSize = list.reduce((sum, item) => sum + item.size, 0);
  const normalizedList = list.map((item) => ({
    ...item,
    size: (item.size / totalSize) * 100
  }));

  const boundaryLabels = [];
  const boundaryPositions = [];
  let cumulative = 0;

  for (const tier of normalizedList) {
    boundaryLabels.push(formatCompactCurrency(tier.lowerRange, currency));
    boundaryPositions.push(cumulative);
    cumulative += tier.size;
  }

  const last = normalizedList[normalizedList.length - 1];
  boundaryLabels.push(
    last.isInfinite
      ? 'Infinite'
      : formatCompactCurrency(last.upperRange, currency)
  );
  boundaryPositions.push(100);

  return {list: normalizedList, boundaryLabels, boundaryPositions};
};
